import React, { useEffect, useState } from 'react';
import { ApiService } from 'services/ApiService';
import type { InvoiceEnrollment } from 'models/enrollments/InvoiceEnrollment';
import type { PaymentFinalizeContext } from 'models/payments/PaymentFinalizeContext';

export interface FinalizeInvoiceFormProps {
  enrollment: InvoiceEnrollment;
  /** Called after a successful finalize so the parent can refresh the enrollment. */
  onFinalized: () => void;
  isDisabled?: boolean;
}

const formatCurrency = (minorUnits: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(minorUnits / 100);

/**
 * Team-facing form shown in the Ops Hub when an invoice node is awaiting team input.
 * Collects the per-client total, previews the deposit credit that will be subtracted,
 * and submits to the finalize endpoint. Only after that can the client view and pay.
 */
export const FinalizeInvoiceForm: React.FC<FinalizeInvoiceFormProps> = ({
  enrollment,
  onFinalized,
  isDisabled = false,
}) => {
  const [totalUsd, setTotalUsd] = useState('');
  const [description, setDescription] = useState(enrollment.description ?? '');
  const [context, setContext] = useState<PaymentFinalizeContext | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    ApiService.getPaymentFinalizeContext(enrollment.id)
      .then((ctx) => { if (!cancelled) setContext(ctx); })
      .catch(() => { /* preview is best-effort; the backend still applies the credit */ });
    return () => { cancelled = true; };
  }, [enrollment.id]);

  const currency = enrollment.currency || 'usd';
  const parsedTotal = parseFloat(totalUsd);
  const isValid = !Number.isNaN(parsedTotal) && parsedTotal > 0;
  const totalMinorUnits = isValid ? Math.round(parsedTotal * 100) : 0;

  const depositCredit = context?.applyDepositCredit
    ? context?.succeededDepositMinorUnits ?? 0
    : 0;
  const netDue = Math.max(0, totalMinorUnits - depositCredit);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await ApiService.finalizeInvoice(enrollment.id, {
        totalMinorUnits,
        currency,
        // Auto-charge is no longer offered in the UI: it depended on a Stripe hosted-invoice
        // "charge_automatically" mechanism that was removed with the Checkout Session rework
        // (no saved-payment-method infrastructure ever backed it). Always false going forward.
        autoCharge: false,
        description: description.trim() || undefined,
      });
      onFinalized();
    } catch {
      setError('Could not finalize the invoice. Please check the amount and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="p-4 bg-white border border-teal-200 rounded-lg shadow-sm space-y-4"
      aria-label="Finalize invoice"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-800">Set Up Invoice</h2>
        <span className="inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold bg-teal-100 text-teal-700 border border-teal-300">
          Team action needed
        </span>
      </div>

      <p className="text-sm text-gray-600">
        Enter the invoice total for this client. Any security deposit they already paid is
        subtracted automatically. Once you confirm, the client receives the invoice and can pay
        through Stripe.
      </p>

      <div>
        <label className="block text-xs text-gray-600 mb-1" htmlFor={`invoice-total-${enrollment.id}`}>
          Invoice total ({currency.toUpperCase()})
        </label>
        <input
          id={`invoice-total-${enrollment.id}`}
          type="number"
          min="0.01"
          step="0.01"
          value={totalUsd}
          onChange={(e) => setTotalUsd(e.target.value)}
          placeholder="e.g. 1200.00"
          disabled={isDisabled || submitting}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      {/* Deposit credit preview */}
      {isValid && depositCredit > 0 && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 divide-y divide-gray-200" aria-label="Invoice preview">
          <div className="flex items-center justify-between px-4 py-2 text-sm">
            <span className="text-gray-600">Invoice total</span>
            <span className="font-medium text-gray-800">{formatCurrency(totalMinorUnits, currency)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2 text-sm">
            <span className="text-gray-600">Deposit already paid</span>
            <span className="font-medium text-green-700">&minus;{formatCurrency(depositCredit, currency)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2 text-sm font-semibold">
            <span className="text-gray-900">Client will owe</span>
            <span className="text-gray-900">{formatCurrency(netDue, currency)}</span>
          </div>
        </div>
      )}

      {context?.applyDepositCredit && depositCredit === 0 && (
        <p className="text-xs text-gray-500">
          No paid security deposit was found for this project, so no credit will be applied.
        </p>
      )}

      <div>
        <label className="block text-xs text-gray-600 mb-1" htmlFor={`invoice-description-${enrollment.id}`}>
          Description (optional — shown to the client)
        </label>
        <input
          id={`invoice-description-${enrollment.id}`}
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Final project invoice"
          disabled={isDisabled || submitting}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50"
        />
      </div>

      {error && (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      )}

      <button
        type="submit"
        disabled={isDisabled || submitting || !isValid}
        className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting && (
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        )}
        {submitting ? 'Finalizing…' : 'Confirm & Send Invoice'}
      </button>
    </form>
  );
};

export default FinalizeInvoiceForm;
